import { defineMessages, MessageDescriptor } from 'react-intl';
import type { StudioFormat, StudioThemeId } from 'fym-shared/src/music24/studioThemes';

const formats = defineMessages({
  imageName: { id: 'app.sml.promote.studio.format.image.name', defaultMessage: 'Image' },
  imageDescription: {
    id: 'app.sml.promote.studio.format.image.description',
    defaultMessage: 'Stories, posts, banners, thumbnails, cover sizes.',
  },
  imageMeta: { id: 'app.sml.promote.studio.format.image.meta', defaultMessage: 'Still · 0 credits' },
  shortName: { id: 'app.sml.promote.studio.format.short.name', defaultMessage: 'Short video' },
  shortDescription: {
    id: 'app.sml.promote.studio.format.short.descriptionV2',
    defaultMessage: 'Reels, TikToks, Shorts, the lyric moment, loops for the services.',
  },
  shortMeta: { id: 'app.sml.promote.studio.format.short.metaV2', defaultMessage: '8–35 s · 1 or 3 credits' },
  fullName: { id: 'app.sml.promote.studio.format.full.name', defaultMessage: 'Full video' },
  fullDescription: {
    id: 'app.sml.promote.studio.format.full.description',
    defaultMessage: 'The whole song for YouTube: lyric video, visualizer, music video.',
  },
  fullMeta: { id: 'app.sml.promote.studio.format.full.metaV2', defaultMessage: 'Whole song · 16:9 · 3 credits' },
  // The group titles of the one asset screen (design t2).
  imagePlural: { id: 'app.sml.promote.studio.format.image.plural', defaultMessage: 'Images' },
  shortPlural: { id: 'app.sml.promote.studio.format.short.plural', defaultMessage: 'Short videos' },
  fullPlural: { id: 'app.sml.promote.studio.format.full.plural', defaultMessage: 'Full videos' },
});

export interface StudioCopy {
  name: MessageDescriptor;
  description: MessageDescriptor;
}

/** The name, the line, the meta and the group title of each format (designs l1, t2). */
export const studioFormatMessages: Record<
  StudioFormat,
  StudioCopy & { meta: MessageDescriptor; plural: MessageDescriptor }
> = {
  image: {
    name: formats.imageName,
    description: formats.imageDescription,
    meta: formats.imageMeta,
    plural: formats.imagePlural,
  },
  short: {
    name: formats.shortName,
    description: formats.shortDescription,
    meta: formats.shortMeta,
    plural: formats.shortPlural,
  },
  full: {
    name: formats.fullName,
    description: formats.fullDescription,
    meta: formats.fullMeta,
    plural: formats.fullPlural,
  },
};

const themes = defineMessages({
  storyName: { id: 'app.sml.promote.studio.theme.story.name', defaultMessage: 'Story' },
  storyDescription: {
    id: 'app.sml.promote.studio.theme.story.description',
    defaultMessage: 'Pre-save, out now or a lyric line',
  },
  feedPostName: { id: 'app.sml.promote.studio.theme.feedPost.name', defaultMessage: 'Feed post' },
  feedPostDescription: {
    id: 'app.sml.promote.studio.theme.feedPost.description',
    defaultMessage: 'Announce or out now, 4:5 or 1:1',
  },
  bannersName: { id: 'app.sml.promote.studio.theme.banners.name', defaultMessage: 'Banners' },
  bannersDescription: {
    id: 'app.sml.promote.studio.theme.banners.description',
    defaultMessage: 'YouTube, X and Facebook headers',
  },
  spotifyHeaderName: { id: 'app.sml.promote.studio.theme.spotifyHeader.name', defaultMessage: 'Spotify header' },
  spotifyHeaderDescription: {
    id: 'app.sml.promote.studio.theme.spotifyHeader.description',
    defaultMessage: 'The top of your Spotify artist page',
  },
  thumbnailName: { id: 'app.sml.promote.studio.theme.thumbnail.name', defaultMessage: 'Thumbnail' },
  thumbnailDescription: {
    id: 'app.sml.promote.studio.theme.thumbnail.description',
    defaultMessage: 'For the YouTube videos',
  },
  coverSizesName: { id: 'app.sml.promote.studio.theme.coverSizes.name', defaultMessage: 'Cover sizes' },
  coverSizesDescription: {
    id: 'app.sml.promote.studio.theme.coverSizes.description',
    defaultMessage: '3000, 1400 and 640 px',
  },
  lyricCardName: { id: 'app.sml.promote.studio.theme.lyricCard.name', defaultMessage: 'Lyric card' },
  lyricCardDescription: {
    id: 'app.sml.promote.studio.theme.lyricCard.description',
    defaultMessage: 'One line as a shareable image',
  },
  linkPreviewName: { id: 'app.sml.promote.studio.theme.linkPreview.name', defaultMessage: 'Link preview' },
  linkPreviewDescription: {
    id: 'app.sml.promote.studio.theme.linkPreview.description',
    defaultMessage: 'The image chats show for your smartlink',
  },
  aiReelName: { id: 'app.sml.promote.studio.theme.aiReel.name', defaultMessage: 'AI reel' },
  aiReelDescription: {
    id: 'app.sml.promote.studio.theme.aiReel.description',
    defaultMessage: 'AI footage cut on the beat, lyric captions, optional voiceover',
  },
  artworkInMotionName: { id: 'app.sml.promote.studio.theme.artworkInMotion.name', defaultMessage: 'Artwork in motion' },
  artworkInMotionDescription: {
    id: 'app.sml.promote.studio.theme.artworkInMotion.description',
    defaultMessage: 'Your cover comes alive on the beat',
  },
  countdownName: { id: 'app.sml.promote.studio.theme.countdown.name', defaultMessage: 'Countdown' },
  countdownDescription: {
    id: 'app.sml.promote.studio.theme.countdown.description',
    defaultMessage: '7, 3 and 1 days to the release, for stories',
  },
  canvasLoopName: { id: 'app.sml.promote.studio.theme.canvasLoop.name', defaultMessage: 'Canvas loop' },
  canvasLoopDescription: {
    id: 'app.sml.promote.studio.theme.canvasLoop.description',
    defaultMessage: '8 s, no text, for Spotify',
  },
  lyricMomentName: { id: 'app.sml.promote.studio.theme.lyricMoment.name', defaultMessage: 'Lyric moment' },
  lyricMomentDescription: {
    id: 'app.sml.promote.studio.theme.lyricMoment.description',
    defaultMessage: "The hook's lines on screen, word by word",
  },
  motionArtworkName: { id: 'app.sml.promote.studio.theme.motionArtwork.name', defaultMessage: 'Motion artwork' },
  motionArtworkDescription: {
    id: 'app.sml.promote.studio.theme.motionArtwork.description',
    defaultMessage: 'The cover as a loop for Apple Music',
  },
  lyricVideoName: { id: 'app.sml.promote.studio.theme.lyricVideo.name', defaultMessage: 'Lyric video' },
  lyricVideoDescription: {
    id: 'app.sml.promote.studio.theme.lyricVideo.descriptionV2',
    defaultMessage: 'Your lyrics, synced, line by line.',
  },
  visualizerName: { id: 'app.sml.promote.studio.theme.visualizer.name', defaultMessage: 'Visualizer' },
  visualizerDescription: {
    id: 'app.sml.promote.studio.theme.visualizer.descriptionV2',
    defaultMessage: 'Your cover and a picture that moves with the song.',
  },
  musicVideoName: { id: 'app.sml.promote.studio.theme.musicVideo.name', defaultMessage: 'Music video' },
  musicVideoDescription: {
    id: 'app.sml.promote.studio.theme.musicVideo.descriptionV2',
    defaultMessage: 'AI footage cut on the beat, with your lyrics as captions.',
  },
});

/** The name and the line of each theme card (designs l2, l3, l4, n6). */
export const studioThemeMessages: Record<StudioThemeId, StudioCopy> = {
  story: { name: themes.storyName, description: themes.storyDescription },
  feedPost: { name: themes.feedPostName, description: themes.feedPostDescription },
  banners: { name: themes.bannersName, description: themes.bannersDescription },
  spotifyHeader: { name: themes.spotifyHeaderName, description: themes.spotifyHeaderDescription },
  thumbnail: { name: themes.thumbnailName, description: themes.thumbnailDescription },
  coverSizes: { name: themes.coverSizesName, description: themes.coverSizesDescription },
  lyricCard: { name: themes.lyricCardName, description: themes.lyricCardDescription },
  linkPreview: { name: themes.linkPreviewName, description: themes.linkPreviewDescription },
  aiReel: { name: themes.aiReelName, description: themes.aiReelDescription },
  artworkInMotion: { name: themes.artworkInMotionName, description: themes.artworkInMotionDescription },
  countdown: { name: themes.countdownName, description: themes.countdownDescription },
  canvasLoop: { name: themes.canvasLoopName, description: themes.canvasLoopDescription },
  lyricMoment: { name: themes.lyricMomentName, description: themes.lyricMomentDescription },
  motionArtwork: { name: themes.motionArtworkName, description: themes.motionArtworkDescription },
  lyricVideo: { name: themes.lyricVideoName, description: themes.lyricVideoDescription },
  visualizer: { name: themes.visualizerName, description: themes.visualizerDescription },
  musicVideo: { name: themes.musicVideoName, description: themes.musicVideoDescription },
};
